import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import axios from "axios";
import SEO from "@/components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InsightsPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await axios.get(`${API}/articles`);
        setArticles(response.data);
      } catch (error) {
        console.error('Failed to fetch articles:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen pt-24">
      <SEO 
        title="Insights & Resources - Hiring Tips, Recruiter Careers & Industry Data"
        description="Expert insights for startup founders and recruitment professionals. Hiring strategies, SaaS compensation benchmarks, recruiter commission breakdowns, team building guides, and career opportunities in tech recruitment."
        keywords="startup hiring tips, talent advisory insights, SaaS hiring blog, tech hiring trends 2026, startup compensation guide, Series A hiring playbook, engineering team building tips, remote hiring best practices, CTO hiring guide, VP Engineering salary, startup equity guide, SaaS recruiter earnings, recruitment consultant commission, 360 recruitment model, tech recruiter career"
        canonical="https://magnuskor.com/insights"
      />
      {/* Header */}
      <section className="py-16 bg-white border-b border-charcoal-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="text-xs uppercase tracking-[0.2em] text-gold-600 font-medium mb-4">Insights</p>
          <h1 className="font-serif text-4xl md:text-5xl text-charcoal-900 mb-4">
            Insights & Resources
          </h1>
          <p className="text-lg text-charcoal-500 max-w-2xl">
            Expert perspectives for startup founders and recruitment professionals. Hiring strategies, compensation data, and career opportunities in SaaS.
          </p>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          {loading ? (
            <div className="text-center py-12 text-charcoal-500">Loading articles...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {articles.map((article) => (
                <Link
                  key={article.id}
                  to={`/insights/${article.slug}`}
                  className="bg-white border border-charcoal-100 hover:border-charcoal-200 hover:shadow-sm transition-all group block"
                  data-testid={`article-${article.id}`}
                >
                  {article.image_url && (
                    <div className="aspect-[16/10] overflow-hidden">
                      <img loading="lazy"
                        src={article.image_url}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs uppercase tracking-wider text-gold-600">{article.category}</span>
                      <span className="text-xs text-charcoal-400">{article.read_time}</span>
                    </div>
                    <h2 className="font-serif text-xl text-charcoal-900 mb-3 group-hover:text-gold-700 transition-colors leading-snug">
                      {article.title}
                    </h2>
                    <p className="text-sm text-charcoal-500 mb-4">{article.excerpt}</p>
                    <div className="flex items-center justify-between pt-4 border-t border-charcoal-100">
                      <span className="text-xs text-charcoal-400">By {article.author}</span>
                      <span className="inline-flex items-center gap-1 text-sm font-medium text-charcoal-600 group-hover:text-gold-600 transition-colors">
                        Read
                        <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter - hidden until backend is ready
      <section className="py-20 bg-charcoal-900">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="font-serif text-3xl text-white mb-4">
            Stay Ahead of the Curve
          </h2>
          <p className="text-charcoal-400 mb-8">
            Weekly insights on startup hiring delivered to your inbox. No spam, unsubscribe anytime.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-grow px-4 py-3 bg-charcoal-800 border border-charcoal-700 text-white placeholder:text-charcoal-500 focus:border-gold-500 focus:outline-none"
              data-testid="newsletter-email"
            />
            <button className="px-6 py-3 bg-gold-600 hover:bg-gold-500 text-white font-medium transition-colors" data-testid="newsletter-submit">
              Subscribe
            </button>
          </div>
        </div>
      </section>
      */}

      {/* CTA */}
      <section className="py-16 bg-[#F5F5F0]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-serif text-2xl text-charcoal-900 mb-4">
            Ready to Build Your Team?
          </h2>
          <p className="text-charcoal-500 mb-6">
            Let's discuss how we can help you find exceptional talent.
          </p>
          <Link to="/for-startups" className="btn-primary inline-flex items-center gap-2">
            Start a Search
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default InsightsPage;
